<script setup lang="ts">
import { Head } from '@inertiajs/vue3'
import { assetGroups } from '@/pages/investors/credit/categories'
import { expenditureSuggestions } from '@/pages/investors/credit/expenditureSuggestions'
import { incomeSuggestions } from '@/pages/investors/credit/incomeSuggestions'
import { liabilitySuggestions } from '@/pages/investors/credit/liabilitySuggestions'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

type IncomePosition = App.Data.SelfDisclosure.IncomePositionData & {
  latest_year: number | null
  latest_amount: number | null
}
type Applicant = App.Data.User.NaturalPersonData & {
  nationality_label: string | null
  incomes: IncomePosition[]
  assets: App.Data.SelfDisclosure.AssetPositionData[]
  liabilities: App.Data.SelfDisclosure.FreePositionData[]
  expenditures: App.Data.SelfDisclosure.FreePositionData[]
}
type FinancialRow = { key: string; label: string; values: Array<number | null>; institutes?: string[] }

const props = defineProps<{
  applicants: Applicant[]
  children: App.Data.User.NaturalPersonData[]
  generatedAt: string
  blankTemplate?: boolean
}>()

const { d, n, t, te } = useI18n()
const formatDate = (value: string | null | undefined): string => (value ? d(new Date(value)) : '')
const formatMoney = (value: number | null | undefined): string =>
  props.blankTemplate ? '' : value == null ? '—' : n(value, 'euros')
const normalizedKey = (value: string): string => value.trim().toLocaleLowerCase()
const enumLabel = (group: string, value: string | null | undefined): string =>
  value && te(`enums.${group}.${value}`) ? t(`enums.${group}.${value}`) : (value ?? '')
const applicantLabel = (index: number): string =>
  index === 0 ? t('general.applicant_primary') : t('general.applicant', { number: index + 1 })
const signatureLabel = (index: number): string =>
  index === 0 ? t('declaration.signature_primary') : t('declaration.signature', { number: index + 1 })

const childrenLabel = computed(() =>
  props.children
    .map((child) =>
      [[child.first_name, child.last_name].filter(Boolean).join(' '), formatDate(child.birth_date)]
        .filter(Boolean)
        .join(', '),
    )
    .filter(Boolean)
    .join('; '),
)
const applicantNames = computed(() =>
  [...new Set(props.applicants.map((person) => person.last_name).filter(Boolean))].join(', '),
)
const declarationText = computed(() =>
  t(props.applicants.length === 1 ? 'declaration.text_singular' : 'declaration.text_plural'),
)

const personalRows = computed(() => [
  { label: t('personal.last_name'), values: props.applicants.map((person) => person.last_name ?? '') },
  { label: t('personal.first_name'), values: props.applicants.map((person) => person.first_name ?? '') },
  { label: t('personal.address'), values: props.applicants.map((person) => person.address ?? '') },
  { label: t('personal.city'), values: props.applicants.map(() => '') },
  { label: t('personal.resident_since'), values: props.applicants.map((person) => formatDate(person.resident_since)) },
  { label: t('personal.phone'), values: props.applicants.map((person) => person.phone ?? '') },
  {
    label: t('personal.birth'),
    values: props.applicants.map((person) =>
      [formatDate(person.birth_date), person.birth_place].filter(Boolean).join(', '),
    ),
  },
  { label: t('personal.tax_id'), values: props.applicants.map((person) => person.tax_id ?? '') },
  { label: t('personal.nationality'), values: props.applicants.map((person) => person.nationality_label ?? '') },
  {
    label: t('personal.in_germany_since'),
    values: props.applicants.map((person) => (person.nationality !== 'DE' ? formatDate(person.in_germany_since) : '')),
  },
  {
    label: t('personal.employment'),
    values: props.applicants.map((person) => enumLabel('professionType', person.profession)),
  },
  { label: t('personal.occupation'), values: props.applicants.map((person) => person.occupation ?? '') },
  { label: t('personal.employer'), values: props.applicants.map((person) => person.employer ?? '') },
  { label: t('personal.employed_since'), values: props.applicants.map(() => '') },
  {
    label: t('personal.retirement'),
    values: props.applicants.map((person) =>
      person.profession !== 'pensioner' ? formatDate(person.planned_retirement_date) : '',
    ),
  },
  {
    label: t('personal.marital_status'),
    values: props.applicants.map((person) => enumLabel('maritalStatus', person.marital_status)),
  },
  { label: t('personal.property_regime'), values: props.applicants.map(() => '') },
  {
    label: t('personal.children'),
    values: props.applicants.map((_, index) => (index === 0 ? childrenLabel.value : '')),
  },
  { label: t('personal.other_dependants'), values: props.applicants.map(() => '') },
])

const labelOrUnnamed = (label: string | null | undefined): string => label?.trim() || t('general.unnamed')

const incomeRows = (group: string): FinancialRow[] => {
  if (props.blankTemplate)
    return incomeSuggestions[group as keyof typeof incomeSuggestions].map((label) => ({
      key: normalizedKey(label),
      label,
      values: Array(props.applicants.length).fill(null),
    }))

  const rows = new Map<string, FinancialRow>()
  props.applicants.forEach((person, applicantIndex) => {
    person.incomes
      .filter((position) => position.group === group && position.latest_amount != null)
      .forEach((position) => {
        const label = labelOrUnnamed(position.name)
        const key = normalizedKey(label)
        const row = rows.get(key) ?? { key, label, values: Array(props.applicants.length).fill(null) }
        row.values[applicantIndex] = (row.values[applicantIndex] ?? 0) + position.latest_amount!
        rows.set(key, row)
      })
  })
  return [...rows.values()]
}

const freePositionRows = (type: 'expenditures' | 'liabilities'): FinancialRow[] => {
  if (props.blankTemplate) {
    const suggestions = type === 'expenditures' ? expenditureSuggestions : liabilitySuggestions

    return suggestions.map((label) => ({
      key: normalizedKey(label),
      label,
      values: Array(props.applicants.length).fill(null),
    }))
  }

  const rows = new Map<string, FinancialRow>()
  props.applicants.forEach((person, applicantIndex) => {
    person[type]
      .filter((position) => position.amount != null)
      .forEach((position) => {
        const label = labelOrUnnamed(position.name)
        const key = normalizedKey(label)
        const row = rows.get(key) ?? { key, label, values: Array(props.applicants.length).fill(null) }
        row.values[applicantIndex] = (row.values[applicantIndex] ?? 0) + position.amount!
        rows.set(key, row)
      })
  })
  return [...rows.values()]
}

const investmentRows = computed<FinancialRow[]>(() => {
  if (props.blankTemplate)
    return assetGroups.investment.map((type) => ({
      key: type,
      label: t(`assets.types.${type}`),
      values: Array(props.applicants.length).fill(null),
      institutes: Array(props.applicants.length).fill(''),
    }))

  const rows = new Map<string, FinancialRow>()
  props.applicants.forEach((person, applicantIndex) => {
    person.assets
      .filter((position) => position.group === 'investment')
      .forEach((position) => {
        const rawLabel = position.type ?? labelOrUnnamed(position.label)
        const key = normalizedKey(rawLabel)
        const row = rows.get(key) ?? {
          key,
          label: position.type ? t(`assets.types.${position.type}`) : rawLabel,
          values: Array(props.applicants.length).fill(null),
          institutes: Array(props.applicants.length).fill(''),
        }
        row.values[applicantIndex] = (row.values[applicantIndex] ?? 0) + (position.amount ?? 0)
        if (position.institute)
          row.institutes![applicantIndex] = [row.institutes![applicantIndex], position.institute]
            .filter(Boolean)
            .join(', ')
        rows.set(key, row)
      })
  })
  return [...rows.values()]
})

const realEstateRows = computed(() => {
  if (props.blankTemplate)
    return assetGroups.real_estate.map((type) => ({
      key: type,
      label: t(`assets.types.${type}`),
      units: null,
      marketValue: null,
    }))

  const rows = new Map<string, { key: string; label: string; units: number | null; marketValue: number | null }>()
  props.applicants
    .flatMap((person) => person.assets)
    .filter((position) => position.group === 'real_estate')
    .forEach((position) => {
      const rawLabel = position.type ?? labelOrUnnamed(position.label)
      const key = normalizedKey(rawLabel)
      const row = rows.get(key) ?? {
        key,
        label: position.type ? t(`assets.types.${position.type}`) : rawLabel,
        units: 0,
        marketValue: 0,
      }
      row.units = (row.units ?? 0) + (position.units ?? 0)
      row.marketValue = (row.marketValue ?? 0) + (position.market_value ?? 0)
      rows.set(key, row)
    })
  return [...rows.values()]
})

const expenditures = computed(() => freePositionRows('expenditures'))
const liabilities = computed(() => freePositionRows('liabilities'))
const totalIncome = computed(() =>
  props.applicants
    .flatMap((person) => person.incomes)
    .reduce((sum, position) => sum + (position.latest_amount ?? 0), 0),
)
const totalExpenditure = computed(() =>
  props.applicants.flatMap((person) => person.expenditures).reduce((sum, position) => sum + (position.amount ?? 0), 0),
)
const surplus = computed(() => totalIncome.value - totalExpenditure.value)
</script>

<template>
  <div class="document">
    <Head :title="t('title')" />

    <PdfSelfDisclosurePage
      :applicant-names="applicantNames"
      :generated-at="generatedAt"
      :page-number="1"
      :total-pages="5">
      <h1>{{ t('personal.title') }}</h1>
      <table class="data-table">
        <thead>
          <tr>
            <th></th>
            <th v-for="(person, index) in applicants" :key="person.id ?? index">{{ applicantLabel(index) }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in personalRows" :key="row.label">
            <th>{{ row.label }}</th>
            <td v-for="(value, index) in row.values" :key="index">{{ blankTemplate ? '' : value || '—' }}</td>
          </tr>
        </tbody>
      </table>
    </PdfSelfDisclosurePage>

    <PdfSelfDisclosurePage
      :applicant-names="applicantNames"
      :generated-at="generatedAt"
      :page-number="2"
      :total-pages="5">
      <h1>{{ t('income.title') }}</h1>
      <div v-for="group in ['active', 'passive', 'other']" :key="group" class="section-block">
        <h2>{{ t(`income.groups.${group}`) }}</h2>
        <table v-if="incomeRows(group).length" class="data-table">
          <thead>
            <tr>
              <th>{{ t('income.net_income') }}</th>
              <th v-for="(person, index) in applicants" :key="person.id ?? index">{{ applicantLabel(index) }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in incomeRows(group)" :key="row.key">
              <th>{{ row.label }}</th>
              <td v-for="(value, index) in row.values" :key="index" class="money">{{ formatMoney(value) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="empty-state">{{ t(`income.empty.${group}`) }}</p>
      </div>
    </PdfSelfDisclosurePage>

    <PdfSelfDisclosurePage
      :applicant-names="applicantNames"
      :generated-at="generatedAt"
      :page-number="3"
      :total-pages="5">
      <h1>{{ t('expenses.title') }}</h1>
      <table v-if="expenditures.length" class="data-table expenses-table">
        <thead>
          <tr>
            <th>{{ t('expenses.position') }}</th>
            <th v-for="(person, index) in applicants" :key="person.id ?? index">{{ applicantLabel(index) }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in expenditures" :key="row.key">
            <th>{{ row.label }}</th>
            <td v-for="(value, index) in row.values" :key="index" class="money">{{ formatMoney(value) }}</td>
          </tr>
        </tbody>
      </table>
      <p v-else class="empty-state">{{ t('expenses.empty') }}</p>
      <div class="total-row">
        <span>{{ t('expenses.surplus') }}</span
        ><strong>{{ formatMoney(surplus) }}</strong>
      </div>

      <div class="section-block">
        <h2>{{ t('bank.title') }}</h2>
        <table class="data-table manual-table">
          <thead>
            <tr>
              <th>{{ t('bank.name') }}</th>
              <th>{{ t('bank.iban') }}</th>
              <th>{{ t('bank.since') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>&nbsp;</td>
              <td></td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="section-block">
        <h2>{{ t('liabilities.title') }}</h2>
        <p class="footnote callout">{{ t('liabilities.note') }}</p>
        <table v-if="liabilities.length" class="data-table liabilities-table">
          <thead>
            <tr>
              <th>{{ t('liabilities.kind') }}</th>
              <th>{{ t('liabilities.institute') }}</th>
              <th>{{ t('liabilities.original') }}</th>
              <th>{{ t('liabilities.balance') }}</th>
              <th>{{ t('liabilities.installment') }}</th>
              <th>{{ t('liabilities.interest_rate') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in liabilities" :key="row.key"
              ><tr v-for="(value, index) in row.values" v-show="blankTemplate || value !== null" :key="index">
                <td>{{ row.label }}</td>
                <td></td>
                <td></td>
                <td class="money">{{ formatMoney(value) }}</td>
                <td></td>
                <td></td></tr
            ></template>
          </tbody>
        </table>
        <p v-else class="empty-state">{{ t('liabilities.empty') }}</p>
      </div>
    </PdfSelfDisclosurePage>

    <PdfSelfDisclosurePage
      :applicant-names="applicantNames"
      :generated-at="generatedAt"
      :page-number="4"
      :total-pages="5">
      <h1>{{ t('assets.title') }}</h1>
      <div class="section-block">
        <h2>{{ t('assets.investments') }}</h2>
        <table v-if="investmentRows.length" class="data-table asset-table">
          <thead>
            <tr>
              <th>{{ t('assets.investment_kind') }}</th>
              <template v-for="(person, index) in applicants" :key="person.id ?? index"
                ><th>{{ applicantLabel(index) }}</th>
                <th>{{ t('assets.institute') }}</th></template
              >
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in investmentRows" :key="row.key">
              <th>{{ row.label }}</th>
              <template v-for="(value, index) in row.values" :key="index"
                ><td class="money">{{ formatMoney(value) }}</td>
                <td>{{ blankTemplate ? '' : row.institutes?.[index] || '—' }}</td></template
              >
            </tr>
          </tbody>
        </table>
        <p v-else class="empty-state">{{ t('assets.empty_investments') }}</p>
      </div>
      <div class="section-block">
        <h2>{{ t('assets.real_estate') }}</h2>
        <table v-if="realEstateRows.length" class="data-table">
          <thead>
            <tr>
              <th>{{ t('assets.kind') }}</th>
              <th>{{ t('assets.units') }}</th>
              <th>{{ t('assets.market_value') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in realEstateRows" :key="row.key">
              <th>{{ row.label }}</th>
              <td class="money">{{ blankTemplate ? '' : row.units || '—' }}</td>
              <td class="money">{{ formatMoney(row.marketValue) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <th colspan="2">{{ t('assets.total_market_value') }}</th>
              <td class="money">
                {{ formatMoney(realEstateRows.reduce((sum, row) => sum + (row.marketValue ?? 0), 0)) }}
              </td>
            </tr>
          </tfoot>
        </table>
        <p v-else class="empty-state">{{ t('assets.empty_real_estate') }}</p>
      </div>
    </PdfSelfDisclosurePage>

    <PdfSelfDisclosurePage
      :applicant-names="applicantNames"
      :generated-at="generatedAt"
      :page-number="5"
      :total-pages="5">
      <h1>{{ t('payment.title') }}</h1>
      <ul class="payment-questions">
        <li>{{ t('payment.question_one') }}</li>
        <li>{{ t('payment.question_two') }}</li>
      </ul>
      <div class="payment-choice">
        ☐ {{ t('general.no') }} &nbsp;&nbsp;&nbsp;&nbsp; ☐ {{ t('general.yes_details') }}
      </div>
      <table class="data-table manual-table">
        <thead>
          <tr>
            <th>{{ t('payment.reason') }}</th>
            <th v-for="(person, index) in applicants" :key="person.id ?? index">{{ applicantLabel(index) }}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>&nbsp;</td>
            <td v-for="(_, index) in applicants" :key="index"></td>
          </tr>
        </tbody>
      </table>

      <div class="declaration section-block">
        <h2>{{ t('declaration.title') }}</h2>
        <p>☐ {{ declarationText }}</p>
        <table v-for="(person, index) in applicants" :key="person.id ?? index" class="data-table signature-table">
          <thead>
            <tr>
              <th>{{ t('declaration.place_date') }}</th>
              <th>{{ signatureLabel(index) }}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>&nbsp;</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="contact-block">
        <div class="contact-copy">
          <h2>{{ t('contact.title') }}</h2>
          <span class="contact-accent"></span>
          <p>{{ t('contact.text') }}</p>
        </div>
        <ul class="contact-details">
          <li><img src="@assets/images/contact-email.png" alt="" /><span>info@finesthub.de</span></li>
          <li><img src="@assets/images/contact-web.png" alt="" /><span>www.finesthub.de</span></li>
        </ul>
        <div class="qr-block">
          <span>{{ t('contact.qr_text') }}</span>
          <img src="@assets/images/self-disclosure-qr.png" :alt="t('contact.qr_alt')" />
        </div>
      </div>
    </PdfSelfDisclosurePage>
  </div>
</template>

<style scoped>
.document {
  --spec-blue: #0054ce;
  --spec-cyan: #118cc0;
  --spec-green: #08d570;
  --spec-stripe: #f4f6fe;
  --spec-border: #b3b3b3;
  color: var(--color-surface-900);
  font-family: Roboto, sans-serif;
  font-size: 9pt;
  line-height: 1.35;
}
h1 {
  margin: 0 0 1rem;
  color: var(--color-surface-900);
  font-size: 13pt;
  font-weight: 700;
}
h2 {
  margin: 0 0 0.5rem;
  color: var(--color-surface-900);
  font-size: 11pt;
  font-weight: 700;
}
.section-block {
  margin-top: 1.5rem;
  break-inside: avoid;
}
.data-table {
  width: 100%;
  overflow: hidden;
  border: 1px solid var(--spec-border);
  border-collapse: separate;
  border-spacing: 0;
  border-radius: 1.5mm;
  table-layout: fixed;
  break-inside: avoid;
}
.data-table th,
.data-table td {
  border-right: 1px solid var(--spec-border);
  border-bottom: 1px solid var(--spec-border);
  padding: 0.42rem 0.5rem;
  text-align: left;
  vertical-align: top;
}
.data-table tr > :last-child {
  border-right: 0;
}
.data-table tbody tr:last-child > *,
.data-table tfoot tr:last-child > * {
  border-bottom: 0;
}
.data-table thead th {
  background: var(--spec-blue);
  color: var(--color-white);
  font-weight: 700;
}
.data-table thead tr:last-child th {
  border-bottom: 1px solid var(--spec-border);
}
.data-table tbody tr:nth-child(even) > * {
  background: var(--spec-stripe);
}
.data-table tbody th {
  width: 34%;
  font-weight: 400;
}
.data-table tbody tr {
  break-inside: avoid;
}
.expenses-table thead th {
  background: var(--spec-cyan);
}
.money {
  text-align: right !important;
  white-space: nowrap;
}
.empty-state,
.footnote {
  margin: 0.4rem 0;
  color: var(--color-surface-600);
  font-size: 8pt;
}
.total-row {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 0.75rem;
  padding: 0.5rem;
  font-size: 11pt;
}
.manual-table tbody td {
  height: 2rem;
}
.callout {
  border-left: 1mm solid var(--color-surface-800);
  padding-left: 0.5rem;
  color: var(--color-surface-900);
  font-style: italic;
  font-weight: 700;
}
.liabilities-table {
  font-size: 7.5pt;
}
.liabilities-table th,
.liabilities-table td {
  padding: 0.32rem;
}
.asset-table {
  font-size: 8pt;
}
.payment-questions {
  margin: 0 0 0.5rem;
  padding-left: 1.25rem;
}
.payment-choice {
  margin: 0 0 0.75rem 0.5rem;
}
.declaration {
  background: #eceaea;
  padding: 0.75rem;
}
.signature-table {
  margin-top: 1rem;
  background: var(--color-white);
}
.signature-table tbody td {
  height: 4rem;
}
.contact-block {
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr) auto;
  gap: 1rem;
  align-items: center;
  margin-top: 1.5rem;
  border-top: 2px solid var(--spec-blue);
  padding: 1rem;
  background: var(--spec-stripe);
  font-size: 7pt;
  break-inside: avoid;
}
.contact-block h2 {
  margin: 0 0 0.5rem;
  background: transparent;
  line-height: 1.2;
  white-space: pre-line;
}
.contact-accent {
  display: block;
  width: 3rem;
  margin: 0.75rem 0;
  border-top: 2px solid var(--spec-green);
}
.contact-block p {
  margin: 0.4rem 0;
}
.contact-details {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.65rem;
  margin: 0;
  padding: 0;
  list-style: none;
}
.contact-details li {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  white-space: nowrap;
}
.contact-details img {
  width: 2rem;
  height: 2rem;
}
.qr-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 10rem;
  border-left: 1px solid var(--spec-border);
  padding-left: 1.5rem;
  text-align: center;
  font-size: 7pt;
}
.qr-block img {
  width: 5rem;
  height: 5rem;
}
.qr-block span {
  white-space: pre-line;
}
</style>

<style>
@font-face {
  font-family: Roboto;
  font-style: normal;
  font-weight: 400;
  font-display: block;
  src: url('@assets/fonts/Roboto-Regular.ttf') format('truetype');
}
@font-face {
  font-family: Roboto;
  font-style: normal;
  font-weight: 700;
  font-display: block;
  src: url('@assets/fonts/Roboto-Bold.ttf') format('truetype');
}
@page {
  size: A4 portrait;
  margin: 0;
}
@media print {
  html,
  body,
  #app,
  .pdf-page {
    margin: 0 !important;
    padding: 0 !important;
  }
}
.document {
  position: relative;
  width: 210mm;
  height: 1485mm;
}
.document > .self-disclosure-page {
  position: absolute;
  left: 0;
}
.document > .self-disclosure-page:nth-of-type(1) {
  top: 0.5mm;
}
.document > .self-disclosure-page:nth-of-type(2) {
  top: 297.5mm;
}
.document > .self-disclosure-page:nth-of-type(3) {
  top: 594.5mm;
}
.document > .self-disclosure-page:nth-of-type(4) {
  top: 891.5mm;
}
.document > .self-disclosure-page:nth-of-type(5) {
  top: 1188.5mm;
}
</style>

<i18n>
{
  "de": {
    "title": "Selbstauskunft",
    "general": { "field": "Angaben", "applicant_primary": "Antragsteller", "applicant": "Antragsteller {number}", "yes": "Ja, und zwar", "yes_details": "Ja, und zwar", "no": "Nein", "unnamed": "Ohne Bezeichnung" },
    "personal": { "title": "1. Persönliche Angaben", "last_name": "Name (Geburtsname)", "first_name": "Vorname", "address": "Straße, Hausnummer", "city": "PLZ / Ort", "resident_since": "Wohnhaft seit", "phone": "Telefon / Handy", "birth": "Geburtsdatum und Ort", "tax_id": "Steuer ID", "nationality": "Staatsangehörigkeit", "in_germany_since": "In Deutschland seit", "employment": "Arbeitsverhältnis", "occupation": "Beruf, Branche", "employer": "Beschäftigt bei", "employed_since": "Beschäftigt seit", "retirement": "Geplanter Renteneintritt", "marital_status": "Familienstand", "property_regime": "Güterstand", "children": "Kinder (Name / Geburtsdatum)", "other_dependants": "Sonstige unterhaltspflichtige Personen" },
    "income": { "title": "2. Einnahmen (monatlich)", "net_income": "Nettoeinkommen", "groups": { "active": "a. Aktive Einnahmen", "passive": "b. Passive Einnahmen", "other": "c. Sonstige Einkünfte" }, "empty": { "active": "Keine aktiven Einnahmen vorhanden.", "passive": "Keine passiven Einnahmen vorhanden.", "other": "Keine sonstigen Einnahmen vorhanden." } },
    "expenses": { "title": "3. Ausgaben (monatlich)", "position": "Ausgabe", "empty": "Keine Ausgaben vorhanden.", "surplus": "Überschuss:" },
    "bank": { "title": "4. Bankverbindung", "name": "Bank (Name)", "iban": "IBAN", "since": "Geschäftsverbindung seit" },
    "liabilities": { "title": "5. Verbindlichkeiten", "note": "Hinweis: Außer Immobiliendarlehen", "kind": "Position", "institute": "Institut", "original": "Urspr. Saldo EUR", "balance": "Akt. Saldo EUR", "installment": "Mon. Rate EUR", "interest_rate": "Zinssatz %", "empty": "Keine Verbindlichkeiten vorhanden." },
    "assets": { "title": "6. Vermögensauskunft", "investments": "a. Anlagevermögen", "real_estate": "b. Immobilien", "investment_kind": "Art der Vermögenswerte", "kind": "Art", "amount": "Betrag", "institute": "Institut", "units": "Anzahl Einheiten", "market_value": "Marktwert", "total_market_value": "Marktwert gesamt:", "empty_investments": "Kein Anlagevermögen vorhanden.", "empty_real_estate": "Keine Immobilien vorhanden.", "types": { "bank_balance": "Bank- und Sparguthaben", "building_saving_balance": "Bausparguthaben", "securities": "Wertpapiere", "life_insurance": "Lebensversicherungen", "pension_insurance": "Rentenversicherungen", "other_investment": "Sonstige Anlagen", "condominium": "Eigentumswohnung", "single_family_house": "Einfamilienhaus", "apartment_building": "Mehrfamilienhaus", "two_party_house": "Zweifamilienhaus", "office": "Büro", "commercial": "Gewerbe", "other_real_estate": "Sonstige Immobilien" } },
    "payment": { "title": "7. Zahlungsverhalten", "question_one": "Bestehen oder bestanden in den letzten 10 Jahren Mahnverfahren oder Zahlungsklagen, Zwangsvollstreckungen, Verfahren zur Abgabe der eidesstattlichen Versicherung, Insolvenzverfahren?", "question_two": "Gab es in den letzten 6 Monaten Lastschriftrückgaben?", "reason": "Grund" },
    "declaration": { "title": "8. Vollständigkeitserklärung", "text_singular": "Ich bestätige die Richtigkeit und Vollständigkeit der gemachten Angaben.", "text_plural": "Wir bestätigen die Richtigkeit und Vollständigkeit der gemachten Angaben.", "place_date": "Ort, Datum", "signature_primary": "Unterschrift Antragsteller", "signature": "Unterschrift Antragsteller {number}" },
    "contact": { "title": "Ihr Direktkontakt zu erfahrenen\nImmobilieninvestoren.", "text": "FinEstHub verbindet die individuelle Finanzierungsstrategie Ihrer Bank datenbasiert mit dem vollständigen Profil vorgeprüfter Investoren, rein auf Erfolgsbasis. Jetzt Termin vereinbaren (QR-Code scannen).", "qr_alt": "QR-Code zur FinEstHub Website", "qr_text": "Sie sind Banker? Jetzt Termin vereinbaren und mehr über FinEstHub erfahren." }
  },
  "en": {
    "title": "Self-disclosure",
    "general": { "field": "Details", "applicant_primary": "Applicant", "applicant": "Applicant {number}", "yes": "Yes, namely", "yes_details": "Yes, namely", "no": "No", "unnamed": "Unnamed" },
    "personal": { "title": "1. Personal details", "last_name": "Surname (birth name)", "first_name": "First name", "address": "Street, house number", "city": "Postal code / city", "resident_since": "Resident since", "phone": "Telephone / mobile", "birth": "Date and place of birth", "tax_id": "Tax ID", "nationality": "Nationality", "in_germany_since": "In Germany since", "employment": "Employment status", "occupation": "Occupation, industry", "employer": "Employer", "employed_since": "Employed since", "retirement": "Planned retirement", "marital_status": "Marital status", "property_regime": "Matrimonial property regime", "children": "Children (name / date of birth)", "other_dependants": "Other dependants" },
    "income": { "title": "2. Income (monthly)", "net_income": "Net income", "groups": { "active": "a. Active income", "passive": "b. Passive income", "other": "c. Other income" }, "empty": { "active": "No active income available.", "passive": "No passive income available.", "other": "No other income available." } },
    "expenses": { "title": "3. Expenses (monthly)", "position": "Expense", "empty": "No expenses available.", "surplus": "Surplus:" },
    "bank": { "title": "4. Bank details", "name": "Bank (name)", "iban": "IBAN", "since": "Banking relationship since" },
    "liabilities": { "title": "5. Liabilities", "note": "Note: Excluding real-estate loans", "kind": "Position", "institute": "Institution", "original": "Original balance EUR", "balance": "Current balance EUR", "installment": "Monthly installment EUR", "interest_rate": "Interest rate %", "empty": "No liabilities available." },
    "assets": { "title": "6. Asset disclosure", "investments": "a. Investments", "real_estate": "b. Real estate", "investment_kind": "Type of asset", "kind": "Type", "amount": "Amount", "institute": "Institution", "units": "Number of units", "market_value": "Market value", "total_market_value": "Total market value:", "empty_investments": "No investments available.", "empty_real_estate": "No real estate available.", "types": { "bank_balance": "Bank and savings balances", "building_saving_balance": "Building savings balances", "securities": "Securities", "life_insurance": "Life insurance", "pension_insurance": "Pension insurance", "other_investment": "Other investments", "condominium": "Condominium", "single_family_house": "Single-family house", "apartment_building": "Apartment building", "two_party_house": "Two-party house", "office": "Office", "commercial": "Commercial", "other_real_estate": "Other real estate" } },
    "payment": { "title": "7. Payment behavior", "question_one": "In the past 10 years, have there been dunning proceedings or payment claims, enforcement proceedings, declarations in lieu of an oath, or insolvency proceedings?", "question_two": "Have any direct debits been returned in the past 6 months?", "reason": "Reason" },
    "declaration": { "title": "8. Declaration of completeness", "text_singular": "I confirm that the information provided is correct and complete.", "text_plural": "We confirm that the information provided is correct and complete.", "place_date": "Place, date", "signature_primary": "Signature applicant", "signature": "Signature applicant {number}" },
    "contact": { "title": "Your direct connection to experienced\nreal-estate investors.", "text": "FinEstHub uses data to match your bank’s individual financing strategy with the complete profiles of pre-qualified investors, purely on a success-fee basis. Schedule an appointment now (scan the QR code).", "qr_alt": "QR code to the FinEstHub website", "qr_text": "Are you a banker? Schedule an appointment now and learn more about FinEstHub." }
  }
}
</i18n>
